/**
 * InputModeContext - Tracks whether focus originated from mouse or keyboard/remote
 * 
 * This context solves the UX issue where mouse hover triggers focusSelf(),
 * which in turn triggers scroll adjustments intended for keyboard navigation.
 * 
 * Usage:
 * - Components call setMouseFocus(true) before focusSelf() on mouseEnter
 * - Scroll logic checks isMouseFocus and skips scrolling if true
 * - Keyboard/arrow key events reset isMouseFocus to false
 * 
 * LEGACY CSS: Uses ES5-compatible syntax for Smart TV compatibility
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// Create context with default values
var InputModeContext = createContext({
    isMouseFocus: false,
    isMouseFocusRef: { current: false },
    setMouseFocus: function () { },
});

/**
 * InputModeProvider - Wrap app with this to enable input mode tracking
 */
export function InputModeProvider({ children }) {
    var _state = useState(false);
    var isMouseFocus = _state[0];
    var setIsMouseFocus = _state[1];

    // Ref mirrors state for synchronous reads inside callbacks.
    // React state updates are async/batched, so when handleMouseEnter calls
    // setMouseFocus(true) then focusSelf(), the onAssetFocus callback would
    // still see the old isMouseFocus=false via its closure. The ref updates
    // immediately, solving the stale-closure problem.
    var isMouseFocusRef = useRef(false);

    // Memoized setter to prevent unnecessary re-renders
    var setMouseFocus = useCallback(function (value) {
        isMouseFocusRef.current = value; // sync update for immediate reads
        setIsMouseFocus(value);
    }, []);

    // Reset mouse focus on any keyboard navigation (arrow keys, enter, back)
    useEffect(function () {
        var handleKeyDown = function (event) {
            var navigationKeys = [
                'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
                'Enter', 'Escape', 'Backspace', 'Back',
                // TV remote codes
                38, 40, 37, 39, 13, 27, 8, 461
            ];

            var key = event.key || event.keyCode;

            if (navigationKeys.indexOf(key) !== -1) {
                isMouseFocusRef.current = false; // sync reset
                setIsMouseFocus(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return function () {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    var contextValue = {
        isMouseFocus: isMouseFocus,
        isMouseFocusRef: isMouseFocusRef,
        setMouseFocus: setMouseFocus,
    };

    return (
        <InputModeContext.Provider value={contextValue}>
            {children}
        </InputModeContext.Provider>
    );
}

/**
 * useInputMode - Hook to access input mode context
 * @returns {{ isMouseFocus: boolean, setMouseFocus: (value: boolean) => void }}
 */
export function useInputMode() {
    return useContext(InputModeContext);
}

export default InputModeContext;
